import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, BookOpen, Home, Leaf, Sparkles } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const impactAreas = [{
  icon: Users,
  title: 'Scholarship Fund',
  description: 'Make meditation accessible to those who cannot afford course fees',
  amount: '$50/month'
}, {
  icon: Home,
  title: 'Facility Maintenance',
  description: 'Keep our meditation halls and accommodations in pristine condition',
  amount: '$100/month'
}, {
  icon: BookOpen,
  title: 'Teaching Programs',
  description: 'Support free community classes and online meditation resources',
  amount: '$75/month'
}, {
  icon: Leaf,
  title: 'Organic Farm',
  description: 'Sustain our farm that provides fresh, sattvic meals for all retreats',
  amount: '$40/month'
}, {
  icon: Sparkles,
  title: 'Teacher Training',
  description: 'Fund the development of new meditation instructors worldwide',
  amount: '$150/month'
}, {
  icon: Heart,
  title: 'General Support',
  description: 'Contribute to wherever the need is greatest',
  amount: 'Any amount'
}];
export function DonatePage() {
  return <div className="min-h-screen bg-[#FFFBF5]" data-id="element-608">
      <Header data-id="element-609" />

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] w-full overflow-hidden" data-id="element-610">
        <motion.div initial={{
        scale: 1.1,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 1.2,
        ease: 'easeOut'
      }} className="absolute inset-0" data-id="element-611">
          <img src="https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?q=80&w=2070&auto=format&fit=crop" alt="Hands holding light" className="w-full h-full object-cover" data-id="element-612" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#FFFBF5]" data-id="element-613" />
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 pb-16" data-id="element-614">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-615">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="max-w-4xl" data-id="element-616">
              <h1 className="font-serif text-5xl md:text-7xl text-white mb-6 leading-tight drop-shadow-2xl" data-id="element-617">
                Support Our Mission
              </h1>
              <p className="text-xl md:text-2xl text-white/95 leading-relaxed drop-shadow-lg" data-id="element-618">
                Your generosity helps spread peace and wisdom to the world
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Statement */}
      <section className="py-20 bg-white" data-id="element-619">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-620">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-621">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-8 text-center" data-id="element-622">
              Your Donation Makes a Difference
            </h2>

            <div className="prose prose-lg max-w-none mb-12" data-id="element-623">
              <p className="text-lg text-[#5D6D7E] leading-relaxed mb-6" data-id="element-624">
                Supradhyan Enlightenment Center operates as a non-profit
                organization dedicated to making authentic spiritual teachings
                accessible to all, regardless of financial circumstances. Your
                generous donations enable us to:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-625">
              <div className="text-center" data-id="element-626">
                <div className="text-4xl font-serif font-bold text-[#8B9D83] mb-2" data-id="element-627">
                  500+
                </div>
                <div className="text-[#5D6D7E]" data-id="element-628">
                  Scholarships Awarded Annually
                </div>
              </div>
              <div className="text-center" data-id="element-629">
                <div className="text-4xl font-serif font-bold text-[#8B9D83] mb-2" data-id="element-630">
                  2,000+
                </div>
                <div className="text-[#5D6D7E]" data-id="element-631">Free Community Classes</div>
              </div>
              <div className="text-center" data-id="element-632">
                <div className="text-4xl font-serif font-bold text-[#8B9D83] mb-2" data-id="element-633">
                  50+
                </div>
                <div className="text-[#5D6D7E]" data-id="element-634">Teachers Trained Each Year</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-635">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-636">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-637">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-4 text-center" data-id="element-638">
              Where Your Donation Goes
            </h2>
            <p className="text-center text-[#5D6D7E] mb-12 max-w-2xl mx-auto" data-id="element-639">
              Choose a specific area to support or contribute to our general
              fund
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-640">
              {impactAreas.map((area, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white p-6 rounded-xl shadow-md" data-id="element-641">
                  <div className="w-12 h-12 rounded-full bg-[#8B9D83]/20 flex items-center justify-center mb-4" data-id="element-642">
                    <area.icon className="w-6 h-6 text-[#8B9D83]" data-id="element-643" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-2" data-id="element-644">
                    {area.title}
                  </h3>
                  <p className="text-[#5D6D7E] mb-4" data-id="element-645">{area.description}</p>
                  <div className="text-sm text-[#D4AF37] font-semibold" data-id="element-646">
                    Suggested: {area.amount}
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-20 bg-white" data-id="element-647">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-648">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-649">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-12 text-center" data-id="element-650">
              Make a Donation
            </h2>

            <div className="bg-[#FAF7F2] p-8 rounded-2xl shadow-lg" data-id="element-651">
              {/* Donation Type */}
              <div className="mb-8" data-id="element-652">
                <label className="block text-sm font-medium text-[#2C3E50] mb-4" data-id="element-653">
                  Donation Type
                </label>
                <div className="grid grid-cols-2 gap-4" data-id="element-654">
                  <button className="py-3 px-6 bg-[#8B9D83] text-white rounded-lg font-semibold" data-id="element-655">
                    One-Time
                  </button>
                  <button className="py-3 px-6 bg-white text-[#8B9D83] border-2 border-[#8B9D83] rounded-lg font-semibold hover:bg-[#8B9D83] hover:text-white transition-colors" data-id="element-656">
                    Monthly
                  </button>
                </div>
              </div>

              {/* Amount Selection */}
              <div className="mb-8" data-id="element-657">
                <label className="block text-sm font-medium text-[#2C3E50] mb-4" data-id="element-658">
                  Select Amount
                </label>
                <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-659">
                  {['$25', '$50', '$100', '$250', '$500', '$1000'].map(amount => <button key={amount} className="py-3 px-4 bg-white border-2 border-gray-200 rounded-lg font-semibold hover:border-[#8B9D83] hover:bg-[#8B9D83] hover:text-white transition-colors" data-id="element-660">
                        {amount}
                      </button>)}
                </div>
                <input type="number" placeholder="Custom amount" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" data-id="element-661" />
              </div>

              {/* Impact Area */}
              <div className="mb-8" data-id="element-662">
                <label className="block text-sm font-medium text-[#2C3E50] mb-2" data-id="element-663">
                  Support Area (Optional)
                </label>
                <select className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors bg-white" data-id="element-664">
                  <option data-id="element-665">General Support</option>
                  <option data-id="element-666">Scholarship Fund</option>
                  <option data-id="element-667">Facility Maintenance</option>
                  <option data-id="element-668">Teaching Programs</option>
                  <option data-id="element-669">Organic Farm</option>
                  <option data-id="element-670">Teacher Training</option>
                </select>
              </div>

              {/* Personal Info */}
              <div className="space-y-4 mb-8" data-id="element-671">
                <input type="text" placeholder="Full Name" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" data-id="element-672" />
                <input type="email" placeholder="Email Address" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B9D83] transition-colors" data-id="element-673" />
              </div>

              <button className="w-full bg-[#8B9D83] hover:bg-[#7A8C72] text-white py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg" data-id="element-674">
                Proceed to Payment
              </button>

              <p className="text-xs text-[#5D6D7E] text-center mt-6" data-id="element-675">
                Your donation is tax-deductible. You will receive a receipt via
                email.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Other Ways to Give */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-676">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-677">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-678">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-12 text-center" data-id="element-679">
              Other Ways to Give
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-680">
              <div className="bg-white p-6 rounded-xl text-center" data-id="element-681">
                <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3" data-id="element-682">
                  Legacy Giving
                </h3>
                <p className="text-[#5D6D7E] mb-4" data-id="element-683">
                  Include Supradhyan in your will or estate planning
                </p>
                <a href="#" className="text-[#8B9D83] font-semibold hover:text-[#7A8C72]" data-id="element-684">
                  Learn More →
                </a>
              </div>

              <div className="bg-white p-6 rounded-xl text-center" data-id="element-685">
                <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3" data-id="element-686">
                  Stock Donations
                </h3>
                <p className="text-[#5D6D7E] mb-4" data-id="element-687">
                  Donate appreciated securities for tax benefits
                </p>
                <a href="#" className="text-[#8B9D83] font-semibold hover:text-[#7A8C72]" data-id="element-688">
                  Learn More →
                </a>
              </div>

              <div className="bg-white p-6 rounded-xl text-center" data-id="element-689">
                <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3" data-id="element-690">
                  Corporate Matching
                </h3>
                <p className="text-[#5D6D7E] mb-4" data-id="element-691">
                  Double your impact through employer matching programs
                </p>
                <a href="#" className="text-[#8B9D83] font-semibold hover:text-[#7A8C72]" data-id="element-692">
                  Learn More →
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-693" />
    </div>;
}